import { categoryOrder } from '@/components/admin/navigation/adminRoutes';

export const ADMIN_USER_CACHE_KEY = 'adminUser';

export const DEFAULT_ADMIN_PREFERENCES = Object.freeze({
  appearance: 'system',
  display_size: 'default',
  receive_release_notes: false,
  sidebar_mode: 'rail',
  navigation_order: Object.freeze([...categoryOrder]),
  navigation_hidden: Object.freeze([]),
});

export const SIDEBAR_MODE_OPTIONS = Object.freeze([
  { value: 'pinned', label: 'Pinned', description: 'Keep navigation open and resize the page.' },
  { value: 'rail', label: 'Rail', description: 'Use a compact rail that expands on intent.' },
  { value: 'overlay', label: 'Overlay', description: 'Keep the page full width and open navigation when needed.' },
]);

export const DISPLAY_SIZE_OPTIONS = Object.freeze([
  { value: 'compact', label: 'Compact', scale: '95%' },
  { value: 'default', label: 'Default', scale: '100%' },
  { value: 'comfortable', label: 'Comfortable', scale: '116%' },
  { value: 'large', label: 'Large', scale: '132%' },
  { value: 'largest', label: 'Largest', scale: '148%' },
]);

const APPEARANCES = new Set(['system', 'light', 'dark']);
const DISPLAY_SIZES = new Set(DISPLAY_SIZE_OPTIONS.map(({ value }) => value));
const SIDEBAR_MODES = new Set(SIDEBAR_MODE_OPTIONS.map(({ value }) => value));
const NAVIGATION_CATEGORIES = new Set(categoryOrder);

const normalizeNavigationOrder = (order) => {
  const normalized = Array.isArray(order)
    ? order.filter((categoryId, index) => (
      NAVIGATION_CATEGORIES.has(categoryId) && order.indexOf(categoryId) === index
    ))
    : [];
  return [...normalized, ...categoryOrder.filter((categoryId) => !normalized.includes(categoryId))];
};

const normalizeHiddenCategories = (hidden) => (
  Array.isArray(hidden)
    ? hidden.filter((categoryId, index) => (
      NAVIGATION_CATEGORIES.has(categoryId) && hidden.indexOf(categoryId) === index
    ))
    : []
);

export const normalizeAdminPreferences = (preferences = {}) => ({
  appearance: APPEARANCES.has(preferences.appearance)
    ? preferences.appearance
    : DEFAULT_ADMIN_PREFERENCES.appearance,
  display_size: DISPLAY_SIZES.has(preferences.display_size)
    ? preferences.display_size
    : DEFAULT_ADMIN_PREFERENCES.display_size,
  receive_release_notes: typeof preferences.receive_release_notes === 'boolean'
    ? preferences.receive_release_notes
    : DEFAULT_ADMIN_PREFERENCES.receive_release_notes,
  sidebar_mode: SIDEBAR_MODES.has(preferences.sidebar_mode)
    ? preferences.sidebar_mode
    : DEFAULT_ADMIN_PREFERENCES.sidebar_mode,
  navigation_order: normalizeNavigationOrder(preferences.navigation_order),
  navigation_hidden: normalizeHiddenCategories(preferences.navigation_hidden),
});

export const getCachedAdminUser = () => {
  try {
    return JSON.parse(localStorage.getItem(ADMIN_USER_CACHE_KEY) || '{}');
  } catch {
    return {};
  }
};

export const getCachedAdminPreferences = () => {
  const user = getCachedAdminUser();
  return normalizeAdminPreferences({
    ...user.preferences,
    receive_release_notes: user.preferences?.receive_release_notes
      ?? user.receive_release_notes,
  });
};

export const applyAdminDisplaySize = (displaySize) => {
  if (typeof document === 'undefined') return;
  const normalized = DISPLAY_SIZES.has(displaySize) ? displaySize : 'default';
  document.documentElement.dataset.adminDisplaySize = normalized;
};

export const cacheAdminPreferences = (preferences, account = null) => {
  const normalized = normalizeAdminPreferences(preferences);
  const existing = getCachedAdminUser();
  const nextUser = {
    ...existing,
    ...(account || {}),
    preferences: normalized,
    receive_release_notes: normalized.receive_release_notes,
  };
  localStorage.setItem(ADMIN_USER_CACHE_KEY, JSON.stringify(nextUser));
  applyAdminDisplaySize(normalized.display_size);
  window.dispatchEvent(new CustomEvent('admin-preferences-changed', { detail: normalized }));
  return nextUser;
};

export const applyCachedAdminDisplaySize = () => {
  applyAdminDisplaySize(getCachedAdminPreferences().display_size);
};
